import type { Type } from '@mend/types/appointment';
import type {
  GetActiveProviderStatsParams,
  GetAggregatedAppointmentDataParams,
  GetAggregatedFormDataParams,
  GetAppointmentSnapshotDataParams,
  GetFormCompletionTimeParams,
  GetFormsAssignedParams,
  GetFormsCompletedParams,
  GetFormsCompletionRateParams,
  GetFormSnapshotDataParams,
  GetNoShowRateParams,
  GetTotalAppointmentsParams,
  GetTotalPatientsSeenParams,
} from '#services/api/insights.ts';

import { queryOptions } from '@tanstack/react-query';

import { getAppointmentTypes } from '#services/api/appointment-type.ts';
import { getAssessments } from '#services/api/assessment.ts';
import {
  getActiveProviders,
  getActiveProviderStats,
  getAppointmentRates,
  getAppointmentsByType,
  getAppointmentSnapshotData,
  getFormCompletionByType,
  getFormCompletionTime,
  getFormCompletionTimeTrends,
  getFormsAssigned,
  getFormsCompleted,
  getFormsCompletionRate,
  getFormSnapshotData,
  getFormStatusCounts,
  getFormTrends,
  getFormTypeBreakdown,
  getNoShowRate,
  getTotalAppointments,
  getTotalPatientsSeen,
} from '#services/api/insights.ts';
import { getProperties } from '#services/api/properties.ts';
import { getMe } from '#services/api/user.ts';

export const me = queryOptions({
  queryKey: ['users', 'me'],
  queryFn: getMe,
  staleTime: Infinity,
});

export const properties = queryOptions({
  queryKey: ['properties'],
  queryFn: getProperties,
  staleTime: Infinity,
});

export function formTrends(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formTrends', params],
    queryFn: () => getFormTrends(params),
    staleTime: Infinity,
  });
}

export function formCompletionByType(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formCompletionByType', params],
    queryFn: () => getFormCompletionByType(params),
    staleTime: Infinity,
  });
}

export function appointmentRates(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['appointmentRates', params],
    queryFn: () => getAppointmentRates(params),
    staleTime: Infinity,
  });
}

export function appointmentsByType(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['appointmentsByType', params],
    queryFn: () => getAppointmentsByType(params),
    staleTime: Infinity,
  });
}

export function noShowRate(params: GetNoShowRateParams) {
  return queryOptions({
    queryKey: ['noShowRate', params],
    queryFn: () => getNoShowRate(params),
    staleTime: Infinity,
  });
}

export function appointmentSnapshot(params: GetAppointmentSnapshotDataParams) {
  return queryOptions({
    queryKey: ['appointmentSnapshot', params],
    queryFn: () => getAppointmentSnapshotData(params),
    staleTime: Infinity,
  });
}

export function totalPatientsSeen(params: GetTotalPatientsSeenParams) {
  return queryOptions({
    queryKey: ['totalPatientsSeen', params],
    queryFn: () => getTotalPatientsSeen(params),
    staleTime: Infinity,
  });
}

export function formCompletionTime(params: GetFormCompletionTimeParams) {
  return queryOptions({
    queryKey: ['formCompletionTime', params],
    queryFn: () => getFormCompletionTime(params),
    staleTime: Infinity,
  });
}

export function activeProviders(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['activeProviders', params],
    queryFn: () => getActiveProviders(params),
    staleTime: Infinity,
  });
}

export function activeProviderStats(params: GetActiveProviderStatsParams) {
  return queryOptions({
    queryKey: ['activeProviderStats', params],
    queryFn: () => getActiveProviderStats(params),
    staleTime: Infinity,
  });
}

export function formCompletionTimeTrends(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formCompletionTimeTrends', params],
    queryFn: () => getFormCompletionTimeTrends(params),
    staleTime: Infinity,
  });
}

export function formTypeBreakdown(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formTypeBreakdown', params],
    queryFn: () => getFormTypeBreakdown(params),
    staleTime: Infinity,
  });
}

export function formStatusCounts(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formStatusCounts', params],
    queryFn: () => getFormStatusCounts(params),
    staleTime: Infinity,
  });
}

export function formSnapshot(params: GetFormSnapshotDataParams) {
  return queryOptions({
    queryKey: ['formSnapshot', params],
    queryFn: () => getFormSnapshotData(params),
    staleTime: Infinity,
  });
}

export function appointmentTypes(type?: Type) {
  return queryOptions({
    queryKey: ['appointment-types', type],
    queryFn: () => getAppointmentTypes(type),
    staleTime: Infinity,
  });
}

export const assessments = queryOptions({
  queryKey: ['assessments'],
  queryFn: getAssessments,
  staleTime: Infinity,
});

export function formsAssigned(params: GetFormsAssignedParams) {
  return queryOptions({
    queryKey: ['formsAssigned', params],
    queryFn: () => getFormsAssigned(params),
    staleTime: Infinity,
  });
}

export function formsCompleted(params: GetFormsCompletedParams) {
  return queryOptions({
    queryKey: ['formsCompleted', params],
    queryFn: () => getFormsCompleted(params),
    staleTime: Infinity,
  });
}

export function formsCompletionRate(params: GetFormsCompletionRateParams) {
  return queryOptions({
    queryKey: ['formsCompletionRate', params],
    queryFn: () => getFormsCompletionRate(params),
    staleTime: Infinity,
  });
}

export function totalAppointments(params: GetTotalAppointmentsParams) {
  return queryOptions({
    queryKey: ['totalAppointments', params],
    queryFn: () => getTotalAppointments(params),
    staleTime: Infinity,
  });
}
